import React, { useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { StatusBadge } from 'components/StatusBadge';
import { useAuth } from 'contexts/AuthContext';
import api from 'lib/api';
import { toast } from 'sonner';
import { BadgeCheck, Upload, FileText } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

const DOC_TYPE_KEYS = {
  id_card: 'documents.type.id_card',
  passport: 'documents.type.passport',
  residence_permit: 'documents.type.residence_permit',
  license: 'documents.type.license',
  student_card: 'documents.type.student_card',
  insurance: 'documents.type.insurance',
  vehicle_registration: 'documents.type.vehicle_registration',
};

export default function DocumentsPage() {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({ document_type: 'id_card', document_url: '', notes: '' });

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, document_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.document_url) { toast.error(t('documents.selectFile')); return; }
    try {
      await api.post('/users/me/documents', form);
      toast.success(t('documents.submitted'));
      refreshUser();
      setForm({ document_type: 'id_card', document_url: '', notes: '' });
    } catch { toast.error(t('documents.error')); }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('documents.title')}</h1>
      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t('documents.status')}</CardTitle></CardHeader>
          <CardContent>
            {user?.is_verified_id ? (
              <div className="flex items-center gap-2 text-emerald-700 p-3 rounded-lg bg-emerald-50">
                <BadgeCheck /> {t('documents.identityVerified')}
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
                {t('documents.identityPending')}
              </div>
            )}
            <h3 className="font-semibold mt-4 mb-2">{t('documents.submittedDocuments')}</h3>
            {(user?.deliverer_documents || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('documents.noneSubmitted')}</p>
            ) : (
              <ul className="space-y-2">
                {(user?.deliverer_documents || []).map((d, i) => (
                  <li key={i} className="flex items-center justify-between p-2 border rounded-md text-sm">
                    <div>
                      <div className="flex items-center gap-2"><FileText size={14}/> {DOC_TYPE_KEYS[d.document_type] ? t(DOC_TYPE_KEYS[d.document_type]) : d.document_type}</div>
                      {d.admin_comment && <p className="text-xs text-destructive mt-1">{t('documents.adminComment')} {d.admin_comment}</p>}
                    </div>
                    <StatusBadge status={d.status || 'pending'} />
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t('documents.submitTitle')}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>{t('documents.typeLabel')}</Label>
              <Select value={form.document_type} onValueChange={v => setForm({...form, document_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="id_card">{t('documents.type.id_card')}</SelectItem>
                  <SelectItem value="passport">{t('documents.type.passport')}</SelectItem>
                  <SelectItem value="residence_permit">{t('documents.type.residence_permit')}</SelectItem>
                  <SelectItem value="license">{t('documents.type.license')}</SelectItem>
                  <SelectItem value="student_card">{t('documents.type.student_card')}</SelectItem>
                  <SelectItem value="insurance">{t('documents.type.insurance')}</SelectItem>
                  <SelectItem value="vehicle_registration">{t('documents.type.vehicle_registration')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('documents.fileLabel')}</Label>
              <Input type="file" accept="image/*,.pdf" onChange={handleFile} data-testid="doc-file-input" />
              {form.document_url && <p className="text-xs text-emerald-700 mt-1">{t('documents.fileLoaded')}</p>}
            </div>
            <div>
              <Label>{t('documents.notesLabel')}</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
            </div>
            <Button onClick={submit} className="w-full" data-testid="doc-submit-button"><Upload size={16} className="mr-1"/> {t('documents.submit')}</Button>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
